import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { downloadFilefromBuffer } from '@/lib/utils';
import { toast } from '@/components/ui/use-toast';
import type {
  PaymentHistoryItem,
  PaymentHistoryQueryParams,
  PaymentHistoryResponse,
} from '@/types/paymentHistory.types';

import axiosInstance from './axiosInstance';

const BASE_URL = '/payment-history';

const buildRequestParams = (params: PaymentHistoryQueryParams) => ({
  ...params,
  clientIds: params.clientIds?.length ? JSON.stringify(params.clientIds) : undefined,
  invoiceNumbers: params.invoiceNumbers?.length ? JSON.stringify(params.invoiceNumbers) : undefined,
});

export const getPaymentHistoryData = async (params: PaymentHistoryQueryParams): Promise<PaymentHistoryResponse> => {
  const response = await axiosInstance.get<PaymentHistoryResponse>(BASE_URL, {
    params: buildRequestParams(params),
  });
  return response.data;
};

export const usePaymentHistory = (
  params: PaymentHistoryQueryParams,
  options?: { query?: Partial<UseQueryOptions<PaymentHistoryResponse>> }
): {
  data: PaymentHistoryItem[];
  totalRows: number;
  totalPages: number;
  currentPage: number;
  isFetching: boolean;
} => {
  const query = useQuery<PaymentHistoryResponse>({
    queryFn: () => getPaymentHistoryData(params),
    queryKey: ['payment-history', params],
    ...options?.query,
  });

  const responseData = query.data;

  return {
    data: responseData?.data ?? [],
    totalRows: responseData?.totalRows ?? 0,
    totalPages: responseData?.totalPages ?? 0,
    currentPage: responseData?.currentPage ?? 1,
    isFetching: query.isFetching,
  };
};

const exportFileNameDateStamp = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
};

export const exportPaymentHistoryCsv = async (params: PaymentHistoryQueryParams): Promise<void> => {
  try {
    const response = await axiosInstance.get<Blob>(`${BASE_URL}/export`, {
      params: buildRequestParams(params),
      responseType: 'blob',
    });

    const fileName = `Payment_History_${exportFileNameDateStamp()}.csv`;
    downloadFilefromBuffer(response.data, fileName, 'CSV');
  } catch {
    toast({
      description: 'Unable to export payment history CSV, please try again',
      duration: 3000,
      variant: 'destructive',
    });
  }
};

export const exportPaymentHistoryPdf = async (params: PaymentHistoryQueryParams): Promise<void> => {
  try {
    const response = await axiosInstance.get<Blob>(`${BASE_URL}/export/pdf`, {
      params: buildRequestParams(params),
      responseType: 'blob',
    });

    const fileName = `Payment_History_${exportFileNameDateStamp()}.pdf`;
    downloadFilefromBuffer(response.data, fileName, 'PDF');
  } catch {
    toast({
      description: 'Unable to export payment history PDF, please try again',
      duration: 3000,
      variant: 'destructive',
    });
  }
};
